import { useEffect, useRef } from 'react';
import { useNotifications, useStore } from '../store/useStore';
import { fetchSubscriptionDraftInvoices } from '../lib/subscriptionInvoiceUtils';
import { APP_ICON } from '../lib/brand';

const AUDIO_PATH = '/sounds/reminder.mp3';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // le bozze nascono di notte: basta un giro ogni 5 minuti

// Pallino sulla voce Fatture + suono + notifica quando ci sono bozze di
// abbonamento in attesa. Suona al primo controllo dopo l'apertura dell'app
// (tipicamente al mattino, con le bozze create dal job notturno) e ogni volta
// che il numero di bozze aumenta.
export const useSubscriptionDraftAlerts = () => {
    const { addNotification } = useNotifications();
    const setSubscriptionDraftCount = useStore((s) => s.setSubscriptionDraftCount);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const lastCountRef = useRef<number | null>(null);

    useEffect(() => {
        const audio = new Audio(AUDIO_PATH);
        audio.preload = 'auto';
        audioRef.current = audio;
    }, []);

    // Sblocca l'audio al primo gesto utente (autoplay policy del browser)
    useEffect(() => {
        const unlock = () => {
            const audio = audioRef.current;
            if (!audio) return;
            audio.play().then(() => { audio.pause(); audio.currentTime = 0; }).catch(() => { /* riprova al trigger */ });
        };
        window.addEventListener('pointerdown', unlock, { once: true });
        window.addEventListener('keydown', unlock, { once: true });
        return () => {
            window.removeEventListener('pointerdown', unlock);
            window.removeEventListener('keydown', unlock);
        };
    }, []);

    useEffect(() => {
        let cancelled = false;

        const fire = (count: number) => {
            const message = `${count} fattur${count === 1 ? 'a' : 'e'} di abbonamento in bozza da controllare ed emettere.`;

            if (audioRef.current) {
                audioRef.current.currentTime = 0;
                audioRef.current.play().catch(() => { /* gesto utente richiesto */ });
            }
            if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
                new Notification('Bozze abbonamenti', { body: message, icon: APP_ICON });
            }
            addNotification({ type: 'warning', title: 'Bozze abbonamenti', message });
        };

        const check = async () => {
            try {
                const drafts = await fetchSubscriptionDraftInvoices();
                if (cancelled) return;

                const count = drafts.length;
                setSubscriptionDraftCount(count);

                const previous = lastCountRef.current;
                lastCountRef.current = count;

                // Primo giro: avvisa se c'è già lavoro pronto. Poi solo sugli aumenti.
                if (count > 0 && (previous === null || count > previous)) {
                    fire(count);
                }
            } catch {
                /* non bloccante */
            }
        };

        check();
        const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [addNotification, setSubscriptionDraftCount]);
};

export default useSubscriptionDraftAlerts;
